import { JobProgress } from '@app/components/base/JobProgress';
import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { videoEditor } from '@mi-front/api-clients';
import { LocaleRelativeTime } from '@mi-front/ui';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import MovieCreationIcon from '@mui/icons-material/MovieCreation';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useFragment } from '../../hooks/useFragment';
import { useRenderedVideoUrl } from './useRenderedVideoUrl';
import { useVideoEditorProjectUrl } from './useVideoEditorProjectUrl';

export interface ProjectCardProps {
  project: videoEditor.Project;
  onClone: () => void;
}

/**
 * Card for a single video editor project: name, status, optional video preview, and actions (open in editor, clone, delete).
 */
export function ProjectCard({ project, onClone }: ProjectCardProps) {
  const { fragment } = useFragment();
  const { $t } = useIntl();
  const queryClient = useQueryClient();
  const editorUrl = useVideoEditorProjectUrl(project.id);
  const { url: renderedUrl, isLoading: isRenderedUrlLoading } = useRenderedVideoUrl(project);
  const isRendered = !!renderedUrl;
  const hasRenderJob = !!project.renderJob;

  const deleteMutation = videoEditor.useProjectsDelete({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: videoEditor.projectsListQueryKey({
            sourceType: 'FRAGMENT',
            sourceId: fragment.id,
          }),
        });
      },
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: project.id });
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {isRendered ? (
          <CardMedia component="div" sx={{ aspectRatio: '16/9', bgcolor: 'grey.900' }}>
            <Box
              component="video"
              src={renderedUrl}
              controls
              sx={{ height: '100%', width: '100%', display: 'block' }}
            />
          </CardMedia>
        ) : isRenderedUrlLoading ? (
          <CardMedia component="div" sx={{ aspectRatio: '16/9', bgcolor: 'grey.900' }} />
        ) : (
          <CardMedia
            component="div"
            sx={{
              aspectRatio: '16/9',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                color: 'grey.400',
              }}
            >
              <VideocamOffIcon sx={{ fontSize: 64 }} />
            </Box>
          </CardMedia>
        )}
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            {project.name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {project.renderJob ? (
                <JobProgress
                  variant="outlined"
                  job={project.renderJob}
                  size="small"
                  statusLabels={{
                    COMPLETED: $t({
                      id: 'fragments.videoEditions.status.generated',
                      defaultMessage: 'Generado',
                    }),
                  }}
                />
              ) : (
                <Chip
                  label={$t({
                    id: 'fragments.videoEditions.status.notGenerated',
                    defaultMessage: 'Sin generar',
                  })}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}
            >
              {project.renderJob?.status === 'COMPLETED' && project.renderJob.finishedOn ? (
                <>
                  {$t({
                    id: 'fragments.videoEditions.finishedOn',
                    defaultMessage: 'Generado',
                  })}{' '}
                  <LocaleRelativeTime value={project.renderJob.finishedOn} />
                </>
              ) : (
                <>
                  {$t({
                    id: 'fragments.videoEditions.updatedAt',
                    defaultMessage: 'Actualizado',
                  })}{' '}
                  <LocaleRelativeTime value={project.updatedAt} />
                </>
              )}
            </Typography>
          </Box>
        </CardContent>
        <CardActions
          sx={{
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 0,
            pl: 2,
          }}
        >
          <Box sx={{ display: 'flex' }}>
            {renderedUrl && (
              <Tooltip
                title={$t({
                  id: 'common.download',
                  defaultMessage: 'Descargar',
                })}
              >
                <IconButton
                  component="a"
                  href={renderedUrl}
                  size="small"
                  rel="noopener noreferrer"
                  aria-label={$t({
                    id: 'common.download',
                    defaultMessage: 'Descargar',
                  })}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={$t({
                id: 'fragments.videoEditions.clone',
                defaultMessage: 'Copiar proyecto',
              })}
            >
              <IconButton
                size="small"
                onClick={onClone}
                aria-label={$t({
                  id: 'fragments.videoEditions.clone',
                  defaultMessage: 'Copiar proyecto',
                })}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isRendered && !hasRenderJob && (
              <ConfirmationAction
                message={$t({
                  id: 'fragments.videoEditions.deleteConfirm',
                  defaultMessage: '¿Eliminar este proyecto? Esta acción no se puede deshacer.',
                })}
                confirmLabel={$t({
                  id: 'common.actions.delete',
                  defaultMessage: 'Eliminar',
                })}
                confirmColor="error"
                onConfirm={handleDelete}
              >
                {({ onClick }) => (
                  <Tooltip
                    title={$t({
                      id: 'common.actions.delete',
                      defaultMessage: 'Eliminar',
                    })}
                  >
                    <IconButton
                      size="small"
                      onClick={onClick}
                      aria-label={$t({
                        id: 'common.actions.delete',
                        defaultMessage: 'Eliminar',
                      })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ConfirmationAction>
            )}
          </Box>
          {!isRendered && !hasRenderJob && (
            <Button
              component="a"
              href={editorUrl}
              rel="noopener noreferrer"
              size="small"
              startIcon={<MovieCreationIcon />}
            >
              {$t({
                id: 'fragments.videoEditions.goToEditor',
                defaultMessage: 'Ir al editor',
              })}
            </Button>
          )}
        </CardActions>
      </Card>
    </Grid>
  );
}
